/**
 * Two-language UI: English + Thai.
 *
 * `t('key')` resolves against the active language from settings. Params use
 * {name} placeholders. Turn instructions themselves are NOT here - Mapbox
 * generates those in Thai when we request language=th, and the voice reads
 * them with a Thai TTS voice.
 */

import { loadSettings } from './settings';

export type Lang = 'en' | 'th';

const STRINGS: Record<string, { en: string; th: string }> = {
  /* login */
  'login.signin': { en: 'Sign in to navigate', th: 'เข้าสู่ระบบเพื่อนำทาง' },
  'login.request': { en: 'Request access', th: 'ขอสิทธิ์เข้าใช้' },
  'login.name': { en: 'Name', th: 'ชื่อ' },
  'login.name.ph': { en: 'Your name', th: 'ชื่อของคุณ' },
  'login.pin': { en: 'PIN', th: 'รหัส PIN' },
  'login.pin.ph': { en: 'Your PIN', th: 'รหัส PIN ของคุณ' },
  'login.pin.new.ph': {
    en: 'Choose a PIN (6+ digits recommended)',
    th: 'ตั้งรหัส PIN (แนะนำ 6 หลักขึ้นไป)',
  },
  'login.submit': { en: 'Sign in', th: 'เข้าสู่ระบบ' },
  'login.wait': { en: 'Please wait…', th: 'กรุณารอสักครู่…' },
  'login.toSignup': { en: 'New driver? Request access', th: 'คนขับใหม่? ขอสิทธิ์เข้าใช้' },
  'login.toLogin': { en: 'Have an account? Sign in', th: 'มีบัญชีแล้ว? เข้าสู่ระบบ' },
  'login.requested': {
    en: 'Access requested. The admin has to approve your account before you can sign in - ask them, then sign in here.',
    th: 'ส่งคำขอแล้ว รอแอดมินอนุมัติบัญชีก่อน จึงจะเข้าสู่ระบบได้',
  },
  'login.pending': {
    en: 'Your account is still waiting for admin approval.',
    th: 'บัญชีของคุณกำลังรอการอนุมัติจากแอดมิน',
  },
  'login.error': { en: 'Something went wrong.', th: 'เกิดข้อผิดพลาด' },

  /* idle / general */
  'idle.whereTo': { en: 'Where to?', th: 'ไปที่ไหน?' },
  'idle.shared': { en: 'Location shared with the fleet', th: 'แชร์ตำแหน่งให้ทีมแล้ว' },
  'idle.shareFailed': { en: 'Could not share - are you online?', th: 'แชร์ไม่สำเร็จ - ออนไลน์อยู่ไหม?' },
  'idle.noFix': { en: 'No GPS fix yet - try again in a moment.', th: 'ยังไม่พบสัญญาณ GPS - ลองอีกครั้ง' },

  /* search */
  'search.ph': { en: 'Search place or paste lat, lng', th: 'ค้นหาสถานที่ หรือวางพิกัด' },
  'search.searching': { en: 'Searching…', th: 'กำลังค้นหา…' },
  'search.noResults': {
    en: 'No results. Try a different spelling or drop a pin on the map.',
    th: 'ไม่พบผลลัพธ์ ลองสะกดใหม่ หรือปักหมุดบนแผนที่',
  },
  'search.failed': { en: 'Search failed. Try again.', th: 'ค้นหาไม่สำเร็จ ลองอีกครั้ง' },
  'search.offline': {
    en: 'You are offline - search needs a connection.',
    th: 'ออฟไลน์อยู่ - การค้นหาต้องใช้อินเทอร์เน็ต',
  },
  'search.empty': {
    en: 'Search for a place, paste coordinates, or long-press the map to drop a pin.',
    th: 'ค้นหาสถานที่ วางพิกัด หรือกดค้างบนแผนที่เพื่อปักหมุด',
  },
  'search.saved': { en: 'Saved places', th: 'สถานที่บันทึกไว้' },
  'search.recent': { en: 'Recent', th: 'ล่าสุด' },
  'search.by': { en: 'by {name}', th: 'โดย {name}' },
  'search.deleteFailed': { en: 'Could not delete.', th: 'ลบไม่สำเร็จ' },

  /* places */
  'place.droppedPin': { en: 'Dropped pin', th: 'หมุดที่ปัก' },
  'place.coordinates': { en: 'Coordinates', th: 'พิกัด' },
  'place.savedBy': { en: 'Saved by {name}', th: 'บันทึกโดย {name}' },
  'place.saveFailed': { en: 'Could not save.', th: 'บันทึกไม่สำเร็จ' },

  /* save dialog + categories */
  'save.title': { en: 'Save place for the fleet', th: 'บันทึกสถานที่ให้ทีม' },
  'save.name.ph': { en: 'Name this place', th: 'ตั้งชื่อสถานที่' },
  'save.save': { en: 'Save', th: 'บันทึก' },
  'cat.home': { en: 'Home', th: 'บ้าน' },
  'cat.work': { en: 'Work', th: 'ที่ทำงาน' },
  'cat.showroom': { en: 'Showroom', th: 'โชว์รูม' },
  'cat.depot': { en: 'Depot', th: 'คลังสินค้า' },
  'cat.other': { en: 'Other', th: 'อื่นๆ' },

  /* preview */
  'preview.finding': { en: 'Finding routes…', th: 'กำลังหาเส้นทาง…' },
  'preview.waitingFix': { en: 'Waiting for a GPS fix…', th: 'กำลังรอสัญญาณ GPS…' },
  'preview.traffic': { en: '+{d} traffic', th: 'รถติด +{d}' },
  'preview.clear': { en: 'traffic clear', th: 'การจราจรคล่องตัว' },
  'preview.eta': { en: 'ETA {t}', th: 'ถึง {t}' },
  'preview.alternative': { en: 'Alternative route', th: 'เส้นทางสำรอง' },
  'route.fastest': { en: 'Fastest', th: 'เร็วที่สุด' },
  'route.no-tolls': { en: 'No tolls', th: 'ไม่ใช้ทางด่วน' },
  'route.no-motorway': { en: 'No motorway', th: 'เลี่ยงมอเตอร์เวย์' },
  'route.usual': { en: 'Your usual way', th: 'เส้นทางที่คุณใช้ประจำ' },
  'preview.avoidTolls': { en: 'Avoid tolls', th: 'เลี่ยงทางด่วน' },
  'preview.avoidMotorways': { en: 'Avoid motorways', th: 'เลี่ยงมอเตอร์เวย์' },
  'preview.from': { en: 'From', th: 'จาก' },
  'preview.here': { en: 'Current location', th: 'ตำแหน่งปัจจุบัน' },
  'preview.reset': { en: 'reset', th: 'ล้าง' },
  'preview.cancel': { en: 'Cancel', th: 'ยกเลิก' },
  'preview.start': { en: 'Start', th: 'เริ่มนำทาง' },

  /* navigation */
  'nav.then': { en: 'then', th: 'จากนั้น' },
  'nav.rerouting': { en: 'Rerouting…', th: 'กำลังหาเส้นทางใหม่…' },
  'nav.rerouteFailed': { en: 'Rerouting failed - retrying…', th: 'หาเส้นทางใหม่ไม่สำเร็จ - กำลังลองใหม่…' },
  'nav.rerouteOffline': {
    en: 'Offline - continue to rejoin the route',
    th: 'ออฟไลน์ - ขับต่อเพื่อกลับเข้าเส้นทาง',
  },
  'nav.arrivedToast': { en: 'You have arrived', th: 'ถึงที่หมายแล้ว' },
  'nav.recenter': { en: 'Re-centre', th: 'กลับสู่ตำแหน่ง' },
  'nav.voice.starting': { en: 'Starting navigation to {name}.', th: 'เริ่มนำทางไป {name}' },
  'nav.voice.updated': { en: 'Route updated.', th: 'ปรับเส้นทางแล้ว' },
  'nav.voice.arrived': { en: 'You have arrived at {name}.', th: 'ถึง {name} แล้ว' },

  /* durations */
  'dur.lessMin': { en: 'less than a min', th: 'ไม่ถึงนาที' },
  'dur.min': { en: '{n} min', th: '{n} นาที' },
  'dur.hr': { en: '{n} hr', th: '{n} ชม.' },
  'dur.hrMin': { en: '{h} hr {m} min', th: '{h} ชม. {m} นาที' },

  /* maneuver sign */
  'sign.on': { en: 'On', th: 'อยู่บน' },
  'sign.then': { en: 'Next', th: 'ถัดไป' },

  /* route stats + AI advisor */
  'stats.signals': { en: '{n} lights', th: 'ไฟแดง {n} จุด' },
  'stats.turns': { en: '{n} turns', th: 'เลี้ยว {n} ครั้ง' },
  'stats.tolls': { en: '{n} tolls', th: 'ด่าน {n} จุด' },
  'stats.calmest': { en: 'Fewest lights', th: 'ไฟแดงน้อยสุด' },
  'stats.atLights': { en: '{d} at lights', th: 'รอไฟแดง {d}' },
  'ai.thinking': { en: 'Checking your past trips…', th: 'กำลังตรวจเส้นทางที่เคยไป…' },
  'ai.badge': { en: 'AI suggestion', th: 'คำแนะนำจาก AI' },
  'ai.use': { en: 'Use this route', th: 'ใช้เส้นทางนี้' },
  'ai.dismiss': { en: 'Dismiss', th: 'ปิด' },

  /* fleet + quick messages
     These are the ONLY place the three questions exist as words. The network
     carries 'food' | 'fuel' | 'help', so each driver reads and hears them in
     their own language regardless of who sent them. */
  'fleet.title': { en: 'Fleet', th: 'ทีมขับรถ' },
  'fleet.nobody': { en: 'No other drivers are out right now.', th: 'ยังไม่มีคนขับคนอื่นออกรถตอนนี้' },
  'fleet.stale': { en: 'no signal', th: 'ไม่มีสัญญาณ' },
  'fleet.ask': { en: 'Ask {name}', th: 'ถาม {name}' },
  'msg.food': { en: 'Food?', th: 'แวะกินข้าวไหม?' },
  'msg.fuel': { en: 'Fuel stop?', th: 'แวะเติมน้ำมันไหม?' },
  'msg.help': { en: 'Help!', th: 'ช่วยด้วย!' },
  'msg.yes': { en: 'Yes', th: 'ตกลง' },
  'msg.no': { en: 'No', th: 'ไม่' },
  'msg.sent': { en: 'Sent to {name}', th: 'ส่งถึง {name} แล้ว' },
  'msg.sendFailed': { en: 'Could not send.', th: 'ส่งไม่สำเร็จ' },
  /* Spoken aloud on arrival, in THIS driver's language and voice. */
  'msg.incoming.voice': { en: '{name} asks: {what}', th: '{name} ถามว่า {what}' },
  'msg.reply.voice': { en: '{name} says {answer}', th: '{name} ตอบว่า {answer}' },
  'msg.reply.toast': { en: '{name}: {answer}', th: '{name}: {answer}' },

  /* road surface (learned from our own drives) */
  'rough.title': { en: 'Rough road', th: 'ถนนขรุขระ' },
  'rough.onRoute': {
    en: '{d} of rough surface on this route',
    th: 'ผิวถนนขรุขระ {d} บนเส้นทางนี้',
  },
  'rough.bad': { en: 'badly broken in places', th: 'ชำรุดหนักบางช่วง' },
  'rough.moderate': { en: 'uneven in places', th: 'ไม่เรียบบางช่วง' },
  'rough.aheadVoice': { en: 'Rough road ahead.', th: 'ข้างหน้าถนนขรุขระ' },
  'rough.aheadToast': { en: 'Rough road in {d}', th: 'ถนนขรุขระในอีก {d}' },
  'set.overlay': { en: 'Roads we have driven', th: 'ถนนที่เราเคยขับ' },
  'set.overlay.off': { en: 'Hidden', th: 'ซ่อน' },
  'set.overlay.speed': { en: 'Colour by speed', th: 'สีตามความเร็ว' },
  'set.overlay.rough': { en: 'Colour by roughness', th: 'สีตามความขรุขระ' },
  'set.learnSpeeds': { en: 'Use my measured speeds', th: 'ใช้ความเร็วที่วัดได้' },
  'set.measured': { en: 'measured {n} km/h', th: 'วัดได้ {n} กม./ชม.' },

  /* route info panel */
  'info.title': { en: 'The route ahead', th: 'เส้นทางข้างหน้า' },
  'info.ahead': { en: 'Ahead', th: 'ข้างหน้า' },
  'info.remaining': { en: 'Distance left', th: 'ระยะทางที่เหลือ' },
  'info.timeLeft': { en: 'Time left', th: 'เวลาที่เหลือ' },
  'info.arrival': { en: 'Arriving', th: 'ถึงเวลา' },
  'info.time': { en: 'Where the time goes', th: 'เวลาหมดไปกับอะไร' },
  'info.driving': { en: 'Driving', th: 'ขับรถ' },
  'info.atLights': { en: 'Waiting at lights', th: 'รอไฟแดง' },
  'info.routerSaid': { en: "Map's own estimate", th: 'ค่าประมาณของแผนที่' },
  'info.terrain': { en: 'Terrain', th: 'ภูมิประเทศ' },
  'info.climb': { en: 'Total climb', th: 'ไต่ระดับรวม' },
  'info.descent': { en: 'Total descent', th: 'ลงระดับรวม' },
  'info.highest': { en: 'Highest point', th: 'จุดสูงสุด' },
  'info.steepestUp': { en: 'Steepest climb', th: 'ทางขึ้นชันสุด' },
  'info.steepestDown': { en: 'Steepest descent', th: 'ทางลงชันสุด' },
  'info.tooSteep': {
    en: 'Too steep for the {name} when loaded - check before committing.',
    th: 'ชันเกินไปสำหรับ {name} เมื่อบรรทุกของ - ตรวจสอบก่อนตัดสินใจ',
  },
  'info.brakeCare': {
    en: '{pct}% over {km} km - use a low gear and watch brake heat.',
    th: '{pct}% ยาว {km} กม. - ใช้เกียร์ต่ำ ระวังเบรกร้อน',
  },
  'info.roads': { en: 'Roads', th: 'ประเภทถนน' },
  'info.stops': { en: 'What will stop you', th: 'สิ่งที่ทำให้ต้องหยุด' },
  'info.signals': { en: 'Traffic lights', th: 'ไฟจราจร' },
  'info.tolls': { en: 'Toll booths', th: 'ด่านเก็บเงิน' },
  'info.crossings': { en: 'Level crossings', th: 'จุดตัดทางรถไฟ' },
  'info.turns': { en: 'Turns', th: 'จุดเลี้ยว' },
  'info.nextFuel': { en: 'Next PTT', th: 'ปตท. ถัดไป' },

  /* gradients */
  'grade.climbFor': { en: '{pct}% climb for {km} km', th: 'ทางขึ้น {pct}% ยาว {km} กม.' },
  'grade.descentFor': { en: '{pct}% descent for {km} km', th: 'ทางลง {pct}% ยาว {km} กม.' },
  'grade.hard': {
    en: 'Hard work for this vehicle - low gear, and watch the brakes coming down.',
    th: 'หนักสำหรับรถคันนี้ - ใช้เกียร์ต่ำ และระวังเบรกตอนลง',
  },
  'grade.blocking': {
    en: 'This is beyond what a loaded vehicle of this type can climb. Check before committing.',
    th: 'ชันเกินกว่าที่รถบรรทุกที่มีน้ำหนักจะขึ้นได้ ตรวจสอบก่อนตัดสินใจ',
  },
  'grade.ascent': { en: ' Total climb {m} m.', th: ' ไต่ระดับรวม {m} ม.' },

  /* PTT fuel stops */
  'fuel.left': { en: 'left side', th: 'ฝั่งซ้าย' },
  'fuel.right': { en: 'right side', th: 'ฝั่งขวา' },
  'set.clock24': { en: '24-hour clock', th: 'เวลาแบบ 24 ชั่วโมง' },
  'sound.title': { en: 'Sound', th: 'เสียง' },
  'sound.voice': { en: 'Voice', th: 'เสียงพูด' },
  'sound.voiceHint': { en: 'Spoken instructions', th: 'บอกทางด้วยเสียง' },
  'sound.alerts': { en: 'Alerts only', th: 'เฉพาะเสียงเตือน' },
  'sound.alertsHint': { en: 'A chime when something needs doing', th: 'เสียงเตือนเมื่อต้องทำอะไร' },
  'sound.muted': { en: 'Muted', th: 'ปิดเสียง' },
  'sound.mutedHint': { en: 'No sound at all', th: 'ไม่มีเสียงเลย' },
  'sound.volume': { en: 'Voice volume', th: 'ระดับเสียงพูด' },
  'sound.test': { en: 'Test', th: 'ทดสอบ' },
  'rough.sensitivity': { en: 'Rough-road sensitivity', th: 'ความไวถนนขรุขระ' },
  'rough.sens.1': { en: 'Only broken roads', th: 'เฉพาะถนนพังจริง' },
  'rough.sens.2': { en: 'Bad roads', th: 'ถนนแย่' },
  'rough.sens.3': { en: 'Normal', th: 'ปานกลาง' },
  'rough.sens.4': { en: 'Sensitive', th: 'ไว' },
  'rough.sens.5': { en: 'Anything bumpy', th: 'ขรุขระนิดหน่อยก็เตือน' },
  'rough.avoid': { en: 'Steer away from rough roads', th: 'เลี่ยงถนนขรุขระ' },
  'rough.avoidHint': {
    en: 'Rough surfaces count against a route, not just warn about it',
    th: 'ถนนขรุขระจะถูกหักคะแนนเส้นทาง ไม่ใช่แค่เตือน',
  },
  'nav.backToStart': { en: 'Back to start', th: 'กลับจุดเริ่มต้น' },
  'nav.backToStartHint': { en: 'Route back to where this trip began', th: 'นำทางกลับจุดที่เริ่มเดินทาง' },
  'fuel.close': { en: 'Close', th: 'ปิด' },
  'fuel.confirm': { en: 'Route via this station?', th: 'ให้แวะปั๊มนี้ไหม?' },
  'fuel.choose': { en: 'Which station?', th: 'เลือกปั๊มไหน?' },
  'fuel.frontage': { en: 'on the frontage road', th: 'อยู่บนทางคู่ขนาน' },
  'fuel.frontageNote': {
    en: 'Turn off before the forecourt - you cannot enter from the main road.',
    th: 'ต้องเลี้ยวออกก่อนถึงปั๊ม เข้าจากถนนหลักไม่ได้',
  },
  'fuel.next': { en: 'Next one instead', th: 'ปั๊มถัดไปแทน' },
  'fuel.go': { en: 'Yes, go there', th: 'ไปที่นั่น' },
  'fuel.detour': { en: 'Detour to {name}', th: 'แวะ {name}' },
  'fuel.undo': { en: 'Undo', th: 'ยกเลิก' },
  'fuel.restored': { en: 'Back on the original route', th: 'กลับสู่เส้นทางเดิม' },
  'fuel.failed': { en: 'Could not route via that station.', th: 'ไม่สามารถวางเส้นทางผ่านปั๊มนี้ได้' },

  /* settings */
  'set.title': { en: 'Settings', th: 'ตั้งค่า' },
  'set.vehicle': { en: 'Vehicle', th: 'ยานพาหนะ' },
  'set.vehicle.car': { en: 'Car', th: 'รถยนต์' },
  'set.vehicle.truck': { en: 'Truck - uses size limits', th: 'รถบรรทุก - ใช้ขนาดจำกัดเส้นทาง' },
  'set.vehicle.addCar': { en: '+ Add vehicle', th: '+ เพิ่มรถ' },
  'set.vehicle.addTruck': { en: '+ Add truck', th: '+ เพิ่มรถบรรทุก' },
  'set.vehicle.edit': { en: 'Edit vehicle', th: 'แก้ไขรถ' },
  'set.vehicle.name': { en: 'Name', th: 'ชื่อ' },
  'set.vehicle.kind': { en: 'Type', th: 'ประเภท' },
  'set.vehicle.delete': { en: 'Delete vehicle', th: 'ลบรถ' },
  'set.vehicle.unnamed': { en: 'Vehicle', th: 'ยานพาหนะ' },
  'set.ai': { en: 'AI route advice', th: 'คำแนะนำเส้นทางจาก AI' },
  'set.easyPass': { en: 'I have Easy Pass / M-Pass', th: 'มี Easy Pass / M-Pass' },
  'live.title': { en: 'Live data', th: 'ข้อมูลสด' },
  'live.speed': { en: 'Speed', th: 'ความเร็ว' },
  'live.roughness': { en: 'Road surface', th: 'ผิวถนน' },
  'live.noSensor': { en: 'no sensor', th: 'ไม่มีเซ็นเซอร์' },
  'live.rough.smooth': { en: 'smooth', th: 'เรียบ' },
  'live.rough.fair': { en: 'fair', th: 'พอใช้' },
  'live.rough.rough': { en: 'rough', th: 'ขรุขระ' },
  'live.rough.bad': { en: 'very bad', th: 'แย่มาก' },
  'live.altitude': { en: 'Altitude', th: 'ความสูง' },
  'live.accuracy': { en: 'GPS accuracy', th: 'ความแม่นยำ GPS' },
  'live.heading': { en: 'Heading', th: 'ทิศทาง' },
  'live.gradeNow': { en: 'Gradient here', th: 'ความชันตรงนี้' },
  'live.roadClass': { en: 'Road class', th: 'ประเภทถนน' },
  'live.tier.motorway': { en: 'Motorway', th: 'มอเตอร์เวย์' },
  'live.tier.dual': { en: 'Main road', th: 'ถนนสายหลัก' },
  'live.tier.single': { en: 'Small road', th: 'ถนนเล็ก' },
  'live.driven': { en: 'Driven', th: 'ขับไปแล้ว' },
  'live.moving': { en: 'Moving', th: 'เคลื่อนที่' },
  'live.samples': { en: 'Samples logged', th: 'จุดที่บันทึก' },
  'live.toTurn': { en: 'To next turn', th: 'ถึงจุดเลี้ยว' },
  'live.offRoute': { en: 'Off route by', th: 'ห่างเส้นทาง' },
  'live.note': {
    en: 'Recorded on every drive and used to improve future routes.',
    th: 'บันทึกทุกครั้งที่ขับ เพื่อปรับปรุงเส้นทางในอนาคต',
  },
  'set.pace': { en: 'Your driving speeds', th: 'ความเร็วที่คุณขับ' },
  'set.pace.hint': {
    en: 'Journey times are worked out from these, not from the speed limit.',
    th: 'เวลาเดินทางคำนวณจากค่านี้ ไม่ใช่จากป้ายจำกัดความเร็ว',
  },
  'set.pace.motorway': { en: 'Motorway (3+ lanes)', th: 'มอเตอร์เวย์ (3 ช่องขึ้นไป)' },
  'set.pace.dual': { en: 'Two-lane main road', th: 'ถนนใหญ่ 2 ช่องทาง' },
  'set.pace.single': { en: 'Single-lane road / soi', th: 'ถนนเลนเดียว / ซอย' },
  'set.pace.signal': { en: 'Time lost per red light', th: 'เวลาที่เสียต่อไฟแดง' },
  'set.route': { en: 'Route options', th: 'ตัวเลือกเส้นทาง' },
  'set.avoidFerries': { en: 'Avoid ferries', th: 'เลี่ยงเรือข้ามฟาก' },
  'set.avoidUnpaved': { en: 'Avoid unpaved roads', th: 'เลี่ยงถนนลูกรัง' },
  'set.useTraffic': { en: 'Live traffic routing', th: 'ใช้ข้อมูลจราจรสด' },
  'set.alternatives': { en: 'Offer alternative routes', th: 'เสนอเส้นทางสำรอง' },
  'set.guidance': { en: 'Guidance', th: 'การนำทาง' },
  'set.voice': { en: 'Voice guidance', th: 'เสียงนำทาง' },
  'set.duck': { en: 'Lower music while speaking', th: 'ลดเสียงเพลงขณะพูด' },
  'set.autozoom': { en: 'Auto-zoom into junctions', th: 'ซูมเข้าทางแยกอัตโนมัติ' },
  'set.awake': { en: 'Keep screen on', th: 'เปิดหน้าจอค้างไว้' },
  'set.overspeed': { en: 'Overspeed warning', th: 'เตือนความเร็วเกิน' },
  'set.overspeed.off': { en: 'Off', th: 'ปิด' },
  'set.display': { en: 'Display', th: 'การแสดงผล' },
  'set.language': { en: 'Language', th: 'ภาษา' },
  'set.theme': { en: 'Map theme', th: 'ธีมแผนที่' },
  'set.theme.auto': { en: 'Auto (sun-based)', th: 'อัตโนมัติ (ตามดวงอาทิตย์)' },
  'set.theme.night': { en: 'Night', th: 'กลางคืน' },
  'set.theme.day': { en: 'Day', th: 'กลางวัน' },
  'set.units': { en: 'Units', th: 'หน่วย' },
  'set.units.km': { en: 'Kilometres', th: 'กิโลเมตร' },
  'set.units.mi': { en: 'Miles', th: 'ไมล์' },
  'set.offline': { en: 'Cache map for offline use', th: 'บันทึกแผนที่ใช้ออฟไลน์' },
  'set.logTrips': { en: 'Record trip history', th: 'บันทึกประวัติการเดินทาง' },
  'set.admin': { en: 'Users (admin)', th: 'ผู้ใช้ (แอดมิน)' },
  'set.admin.loading': { en: 'Loading users…', th: 'กำลังโหลดผู้ใช้…' },
  'set.admin.none': { en: 'No other users yet.', th: 'ยังไม่มีผู้ใช้อื่น' },
  'set.admin.approve': { en: 'Approve', th: 'อนุมัติ' },
  'set.admin.disable': { en: 'Disable', th: 'ระงับ' },
  'set.admin.enable': { en: 'Re-enable', th: 'เปิดใช้อีกครั้ง' },
  'set.admin.resetPin': { en: 'Reset PIN', th: 'รีเซ็ต PIN' },
  'set.admin.newPin': { en: 'New PIN for this user (4-10 digits):', th: 'PIN ใหม่ของผู้ใช้นี้ (4-10 หลัก):' },
  'set.status.pending': { en: 'pending', th: 'รออนุมัติ' },
  'set.status.approved': { en: 'approved', th: 'อนุมัติแล้ว' },
  'set.status.disabled': { en: 'disabled', th: 'ถูกระงับ' },
  'set.account': { en: 'Account', th: 'บัญชี' },
  'set.signedInAs': { en: 'Signed in as', th: 'เข้าสู่ระบบเป็น' },
  'set.admin.tag': { en: ' (admin)', th: ' (แอดมิน)' },
  'set.signOut': { en: 'Sign out', th: 'ออกจากระบบ' },
  'set.failed': { en: 'Failed', th: 'ไม่สำเร็จ' },

  /* trips */
  'trips.title': { en: 'Trips', th: 'ประวัติการเดินทาง' },
  'trips.loading': { en: 'Loading…', th: 'กำลังโหลด…' },
  'trips.empty': {
    en: "No trips yet. Drive somewhere and it'll be logged here.",
    th: 'ยังไม่มีการเดินทาง ขับไปที่ไหนสักแห่งแล้วจะบันทึกไว้ที่นี่',
  },
  'trips.again': { en: 'Navigate again', th: 'นำทางอีกครั้ง' },
  'trips.delete': { en: 'Delete', th: 'ลบ' },
  'trips.details': { en: 'Details', th: 'รายละเอียด' },
  'trips.avgSpeed': { en: 'Avg', th: 'เฉลี่ย' },
  'trips.maxSpeed': { en: 'Max', th: 'สูงสุด' },
  'trips.moving': { en: 'Moving', th: 'เคลื่อนที่' },
  'trips.stopped': { en: 'Stopped', th: 'หยุดนิ่ง' },
  'trips.fuel': { en: 'Fuel est.', th: 'น้ำมันโดยประมาณ' },
  'trips.vsEstimate': { en: 'vs estimate', th: 'เทียบกับที่คาด' },
  'trips.reroutes': { en: '{n} reroutes', th: 'หาเส้นทางใหม่ {n} ครั้ง' },
  'trips.segments': { en: 'Along the way', th: 'ระหว่างทาง' },
  'trips.unknownRoad': { en: 'unnamed road', th: 'ถนนไม่ระบุชื่อ' },

  /* spotify */
  'sp.connect': { en: 'Connect Spotify', th: 'เชื่อมต่อ Spotify' },
  'sp.nothing': { en: 'Nothing playing', th: 'ไม่มีเพลงเล่นอยู่' },
  'sp.on': { en: 'on {device}', th: 'ที่ {device}' },
  'sp.hint': { en: 'Open Spotify or press play', th: 'เปิด Spotify หรือกดเล่น' },
  'sp.playHere': { en: 'Play through this device', th: 'เล่นผ่านอุปกรณ์นี้' },
  'sp.playingHere': { en: 'Playing here', th: 'กำลังเล่นที่นี่' },

  /* gps errors */
  'gps.denied': {
    en: 'Location permission denied. Allow location access for this site in your browser settings.',
    th: 'ไม่ได้รับสิทธิ์ตำแหน่ง กรุณาอนุญาตการเข้าถึงตำแหน่งในตั้งค่าเบราว์เซอร์',
  },
  'gps.unavailable': {
    en: 'Location unavailable. Move to open sky and try again.',
    th: 'ไม่พบตำแหน่ง ลองย้ายไปที่โล่งแจ้ง',
  },
  'gps.waiting': { en: 'Waiting for a GPS fix…', th: 'กำลังรอสัญญาณ GPS…' },
  'gps.error': { en: 'Location error.', th: 'ข้อผิดพลาดตำแหน่ง' },
  'gps.none': { en: 'This device has no location support.', th: 'อุปกรณ์นี้ไม่รองรับตำแหน่ง' },
};

let lang: Lang = loadSettings().language ?? 'en';

export function setLang(l: Lang): void {
  lang = l;
}

export function getLang(): Lang {
  return lang;
}

export function t(key: string, params?: Record<string, string | number>): string {
  const entry = STRINGS[key];
  let s = entry ? entry[lang] : key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replace(`{${k}}`, String(v));
    }
  }
  return s;
}
